import { Button } from "@/components/flexkit/Button";
import { Combobox } from "@/components/flexkit/combobox";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import {
    useAuthenticatedRunnerIds,
    useListRunners,
    useListSCMOrganizationsAcrossRunners,
    useSearchRepositoriesAcrossRunners,
} from "@/queries/runner-queries";
import type { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";
import { SearchMode, RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { useCallback, useMemo, useState, type FC } from "react";
import { useDebounce } from "use-debounce";
import {
    extractRepoPath,
    getScmHost,
    getScopeConfig,
    getWebhookSettingsUrl,
    WEBHOOK_PROVIDER_CONFIG,
    type SCMRepository,
} from "@/components/webhooks/webhook-config";

type WebhookScopeFieldProps = {
    provider: WebhookProvider;
    webhookType: WebhookType;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    readOnly?: boolean;
    /** If true, the settings URL will point to the "new webhook" page */
    isNewWebhook?: boolean;
};

export const WebhookScopeField: FC<WebhookScopeFieldProps> = ({
    provider,
    webhookType,
    value,
    onChange,
    disabled = false,
    readOnly = false,
    isNewWebhook = false,
}) => {
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue] = useDebounce(searchValue, 400, { trailing: true });
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const scmHost = getScmHost(provider);
    const providerName = WEBHOOK_PROVIDER_CONFIG[provider].name;
    const { isOrgType, scopeLabel, comboboxPlaceholder } = getScopeConfig(webhookType);
    const settingsUrl = getWebhookSettingsUrl(provider, webhookType, value, isNewWebhook);

    // Only load data if not read-only
    const shouldLoadData = !readOnly;

    // Get runners for querying: active, non-local runners
    const { runners: allRunners } = useListRunners({ enabled: shouldLoadData });
    const runners = useMemo(
        () => allRunners.filter((r) => r.status?.phase === RunnerPhase.ACTIVE && r.kind !== RunnerKind.LOCAL),
        [allRunners],
    );
    const runnerIds = useMemo(() => runners.map((r) => r.runnerId), [runners]);

    // Pre-filter to only runners authenticated for this SCM host
    const { authenticatedRunnerIds } = useAuthenticatedRunnerIds({
        runnerIds,
        scmHost,
        enabled: shouldLoadData && runnerIds.length > 0 && !!scmHost,
    });

    // Fetch organizations from authenticated runners only
    const { organizations, isFetching: isFetchingOrgs } = useListSCMOrganizationsAcrossRunners({
        runnerIds: authenticatedRunnerIds,
        scmHost,
        enabled: shouldLoadData && isOrgType && authenticatedRunnerIds.length > 0 && !!scmHost,
    });

    // Fetch repositories from authenticated runners only
    const { repositories: repoResults, isLoading: isLoadingRepos } = useSearchRepositoriesAcrossRunners({
        runnerIds: authenticatedRunnerIds,
        query: debouncedSearchValue,
        scmHost,
        searchMode: SearchMode.KEYWORD,
        enabled: shouldLoadData && !isOrgType && authenticatedRunnerIds.length > 0 && !!scmHost,
        pageSize: 50,
    });

    const repositories = useMemo(
        (): SCMRepository[] =>
            repoResults.map((repo) => ({
                name: repo.name,
                url: repo.url,
            })),
        [repoResults],
    );

    // Only update search value while the popover is open.
    // When the popover closes, Combobox resets its internal search to "" —
    // ignoring that prevents a spurious empty-string search after selection.
    const handleSearchValueChanged = useCallback(
        (newValue: string) => {
            if (isPopoverOpen) {
                setSearchValue(newValue);
            }
        },
        [isPopoverOpen],
    );

    const handleOpenChange = useCallback((open: boolean) => {
        setIsPopoverOpen(open);
        if (!open) {
            // Reset search when closing so reopening starts fresh
            setSearchValue("");
        }
    }, []);

    const handleOpenSettings = () => {
        if (settingsUrl) {
            window.open(settingsUrl, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <div className="flex flex-col gap-2">
            <Label htmlFor="webhook-scope" className="text-sm font-medium">
                {scopeLabel}
            </Label>
            <div className="flex items-center gap-1">
                {readOnly ? (
                    <Input
                        id="webhook-scope"
                        value={value}
                        readOnly
                        className="flex-1"
                        data-testid="webhook-scope-input"
                    />
                ) : (
                    <Combobox
                        value={value}
                        onValueChange={onChange}
                        onOpenChange={handleOpenChange}
                        loading={false}
                        className="flex-1"
                        disabled={disabled}
                    >
                        <Combobox.Value>
                            {(val) => (
                                <Combobox.ValueLabel className="max-w-40 truncate">
                                    {val || comboboxPlaceholder}
                                </Combobox.ValueLabel>
                            )}
                        </Combobox.Value>
                        {isOrgType ? (
                            <Combobox.Popover sameWidth={false} className="min-w-[--popover-anchor-width] max-w-96">
                                <Combobox.SearchBox loading={isFetchingOrgs} />
                                {organizations && organizations.length > 0 ? (
                                    <Combobox.List
                                        items={organizations}
                                        searchKeys={["name"]}
                                        noMatchesComponent={<Combobox.Empty>No organizations found</Combobox.Empty>}
                                    >
                                        {(org) => (
                                            <Combobox.ListItem key={org.name} value={org.name} title={org.name} />
                                        )}
                                    </Combobox.List>
                                ) : (
                                    <Combobox.Empty>No organizations found</Combobox.Empty>
                                )}
                            </Combobox.Popover>
                        ) : (
                            <Combobox.Popover sameWidth={false} className="max-w-96">
                                <Combobox.SearchBox
                                    onValueChanged={handleSearchValueChanged}
                                    loading={isLoadingRepos}
                                />
                                {repositories && repositories.length > 0 ? (
                                    <Combobox.List
                                        items={repositories}
                                        searchKeys={[]}
                                        disableFiltering
                                        noMatchesComponent={<Combobox.Empty>No repositories found</Combobox.Empty>}
                                    >
                                        {(repo) => (
                                            <Combobox.ListItem
                                                key={repo.url}
                                                value={extractRepoPath(repo.url)}
                                                title={extractRepoPath(repo.url)}
                                            />
                                        )}
                                    </Combobox.List>
                                ) : (
                                    <Combobox.Empty>No repositories found</Combobox.Empty>
                                )}
                            </Combobox.Popover>
                        )}
                    </Combobox>
                )}
                <Button
                    variant="ghost"
                    onClick={handleOpenSettings}
                    disabled={!settingsUrl || disabled}
                    data-testid="open-webhook-settings"
                    data-tracking-id="open-webhook-settings"
                    className="gap-0 px-3 text-sm"
                >
                    Open&nbsp;{providerName}&nbsp;webhook settings
                </Button>
            </div>
        </div>
    );
};
